import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { MasterTable } from "./MasterTable";
import { MasterToolbar } from "./MasterToolbar";
import { MasterAddForm } from "./MasterAddForm";
import { ImportResultDialog } from "./ImportResultDialog";

export function MasterPanel({ title, icon: Icon, endpoint, fields, testPrefix, bulk = false, importer = false, importHint }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [selected, setSelected] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/${endpoint}`).then(({ data }) => { setItems(data); setSelected([]); }).catch((e) => toast.error(formatApiError(e)));
  }, [endpoint]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/${endpoint}`, form);
      toast.success(`${title.slice(0, -1)} added`);
      setForm({});
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const save = async (id) => {
    try {
      await api.put(`/${endpoint}/${id}`, editForm);
      toast.success("Updated");
      setEditId(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete ${item.name}?`)) return;
    try {
      await api.delete(`/${endpoint}/${item.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.length} selected ${title.toLowerCase()}? This cannot be undone.`)) return;
    try {
      const { data } = await api.post(`/${endpoint}/bulk-delete`, { ids: selected });
      toast.success(`${data.deleted} deleted`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get(`/${endpoint}/import-template`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dcn_${endpoint}_import_template.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setImporting(true);
    try {
      const { data } = await api.post(`/${endpoint}/import`, fd);
      const skipped = data.skipped ?? data.skipped_existing ?? 0;
      toast.success(`${data.added} added, ${skipped} skipped`);
      if (data.skipped_rows?.length) setImportResult(data);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="rounded-md border border-slate-200 bg-white" data-testid={`${testPrefix}-panel`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 p-4">
        <h2 className="flex items-center gap-2 text-base font-bold text-slate-900 md:text-lg">
          <Icon size={18} weight="duotone" /> {title}
        </h2>
        <div className="flex items-center gap-3">
          <MasterToolbar testPrefix={testPrefix} importer={importer} importHint={importHint}
            importing={importing} fileRef={fileRef} onTemplate={downloadTemplate} onImportFile={importExcel}
            showBulkDelete={bulk && selected.length > 0} selectedCount={selected.length} onBulkDelete={bulkDelete} />
          <span className="text-xs text-slate-400">{items.length} entries</span>
        </div>
      </div>
      <MasterAddForm fields={fields} form={form} setForm={setForm} onSubmit={add} testPrefix={testPrefix} />
      <MasterTable items={items} fields={fields} bulk={bulk} testPrefix={testPrefix}
        selected={selected} setSelected={setSelected}
        editId={editId} setEditId={setEditId} editForm={editForm} setEditForm={setEditForm}
        save={save} remove={remove} />
      {importResult && (
        <ImportResultDialog result={importResult} testPrefix={testPrefix} onClose={() => setImportResult(null)} />
      )}
    </div>
  );
}
